(function () {
  class DiyCharmPreview extends HTMLElement {
    constructor() {
      super();
      this.selectedSlots = [];
      this.activeSlotIndex = 1;
      this._activeSlotChangeHandler = this.onActiveSlotChange.bind(this);
      this._selectionChangeHandler = this.onSelectionChange.bind(this);
      this._previewClickHandler = this.onPreviewClick.bind(this);
    }

    connectedCallback() {
      window.addEventListener("diy-charm:active-slot-changed", this._activeSlotChangeHandler);
      window.addEventListener("diy-charm:selection-changed", this._selectionChangeHandler);
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("diy-charm:active-slot-changed", this._activeSlotChangeHandler);
      window.removeEventListener("diy-charm:selection-changed", this._selectionChangeHandler);
      if (this._clickTarget) {
        this._clickTarget.removeEventListener("click", this._previewClickHandler);
        this._clickTarget = null;
      }
    }

    get linkedGroup() {
      return this.querySelector(".diy-charms-arc")?.getAttribute("data-linked-option-group") || "charm";
    }

    // bind current active slot when interacting with custom option groups
    onActiveSlotChange(event) {
      const detail = event.detail || {};
      if (!detail.linkedGroup || detail.linkedGroup.toLowerCase() !== this.linkedGroup.toLowerCase()) return;

      if (!detail.activeSlotIndex) return;

      this.activeSlotIndex = detail.activeSlotIndex;
      this.updateSelectedState();
    }

    onSelectionChange(event) {
      const detail = event.detail || {};
      if (!detail.linkedGroup || detail.linkedGroup.toLowerCase() !== this.linkedGroup.toLowerCase()) return;

      this.selectedSlots = Array.isArray(detail.slots) ? detail.slots : [];

      if (detail.activeSlotIndex) {
        this.activeSlotIndex = detail.activeSlotIndex;
      } else if (this.selectedSlots.length > 0) {
        this.activeSlotIndex = this.selectedSlots[0].slotIndex;
      }

      this.syncSelectedCharms();
      this.updateSelectedState();
    }

    onPreviewClick(event) {
      const clickedItem = event.target.closest(".diy-charm-placeholder__item");
      const clickedPlaceholder = event.target.closest(".diy-charm-placeholder--primary");
      if (!clickedItem && !clickedPlaceholder) return;

      const nextSlotIndex = clickedItem ? parseInt(clickedItem.dataset.slotIndex || "1", 10) : this.activeSlotIndex || 1;
      this.activeSlotIndex = nextSlotIndex;
      this.updateSelectedState();

      this.dispatchEvent(
        new CustomEvent("diy-charm:placeholder-selected", {
          detail: {
            index: nextSlotIndex,
            linkedGroup: this.linkedGroup,
            placeholderEl: clickedItem || clickedPlaceholder,
          },
          bubbles: true,
          composed: true,
        }),
      );
    }

    // Live getter: always resolves against the current DOM so stale refs after
    // gallery.setSlides() (which rebuilds innerHTML) are never an issue.
    get itemsContainer() {
      const trackSelector = this.dataset.galleryTrack;
      if (trackSelector) {
        const track = document.querySelector(trackSelector);
        const firstSlide = track?.firstElementChild;
        if (firstSlide) return firstSlide.querySelector("[data-diy-charm-items]");
      }
      return this.querySelector("[data-diy-charm-items]");
    }

    init() {
      const trackSelector = this.dataset.galleryTrack;
      if (trackSelector) {
        const track = document.querySelector(trackSelector);
        const firstSlide = track?.firstElementChild;
        if (firstSlide) {
          firstSlide.append(...Array.from(this.childNodes));
          // Attach to the track, not firstSlide — track survives setSlides() rebuilds.
          this._clickTarget = track;
          track.addEventListener("click", this._previewClickHandler);
          return;
        }
      }
      this._clickTarget = this;
      this.addEventListener("click", this._previewClickHandler);
    }

    createCharmItem(slot) {
      const itemEl = document.createElement("button");
      itemEl.type = "button";
      itemEl.className = "diy-charm-placeholder__item";

      const imgEl = document.createElement("img");
      imgEl.loading = "lazy";
      itemEl.appendChild(imgEl);

      this.updateCharmItem(itemEl, slot);

      return itemEl;
    }

    updateCharmItem(itemEl, slot) {
      itemEl.dataset.slotIndex = String(slot.slotIndex);
      itemEl.setAttribute("aria-label", slot.name || `Charm slot ${slot.slotIndex}`);

      let imgEl = itemEl.querySelector("img");
      if (!imgEl) {
        imgEl = document.createElement("img");
        imgEl.loading = "lazy";
        itemEl.appendChild(imgEl);
      }

      if (imgEl.src !== slot.src) {
        imgEl.src = slot.src;
      }
      imgEl.alt = slot.name || `Charm ${slot.slotIndex}`;
    }

    getRenderedCharmItems(container) {
      return new Map(
        Array.from(container.querySelectorAll(".diy-charm-placeholder__item")).map((itemEl) => [
          parseInt(itemEl.dataset.slotIndex || "0", 10),
          itemEl,
        ]),
      );
    }

    syncSelectedCharms() {
      const container = this.itemsContainer;
      if (!container) return;

      const renderedItems = this.getRenderedCharmItems(container);

      this.selectedSlots.forEach((slot, index) => {
        let itemEl = renderedItems.get(slot.slotIndex);

        if (itemEl) {
          this.updateCharmItem(itemEl, slot);
          renderedItems.delete(slot.slotIndex);
        } else {
          itemEl = this.createCharmItem(slot);
        }

        const referenceNode = container.children[index] || null;
        if (referenceNode !== itemEl) {
          container.insertBefore(itemEl, referenceNode);
        }
      });

      renderedItems.forEach((itemEl) => {
        itemEl.remove();
      });
    }

    updateSelectedState() {
      const container = this.itemsContainer;
      if (!container) return;

      const itemEls = Array.from(container.querySelectorAll(".diy-charm-placeholder__item"));
      itemEls.forEach((itemEl) => {
        const slotIndex = parseInt(itemEl.dataset.slotIndex || "1", 10);
        itemEl.classList.toggle("selected", slotIndex === this.activeSlotIndex);
      });
    }

  }

  if (!window.customElements.get("diy-charm-preview")) {
    window.customElements.define("diy-charm-preview", DiyCharmPreview);
  }
})();
